import React, { useRef, useState } from 'react';
import { ZodError } from 'zod';

import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';

import { TextProps, TextPropsSchema } from '@usewaypoint/block-text';

import BaseSidebarPanel from './helpers/BaseSidebarPanel';
import BooleanInput from './helpers/inputs/BooleanInput';
import MultiStylePropertyPanel from './helpers/style-inputs/MultiStylePropertyPanel';

type TextSidebarPanelProps = {
  data: TextProps;
  setData: (v: TextProps) => void;
};

export default function TextSidebarPanel({ data, setData }: TextSidebarPanelProps) {
  const [, setErrors] = useState<ZodError | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [text, setText] = useState<string>(data.props?.text ?? '');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('https://');
  const [linkLabel, setLinkLabel] = useState('');
  const selectionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  const updateData = (d: unknown) => {
    const res = TextPropsSchema.safeParse(d);
    if (res.success) {
      setData(res.data);
      setErrors(null);
    } else {
      setErrors(res.error);
    }
  };

  // Markdown collapses single newlines. Add two trailing spaces to each line so
  // every newline becomes a real <br> in the rendered email.
  const preserveBreaks = (s: string) =>
    s
      .split('\n')
      .map((line) => (line.endsWith('  ') || line.trim() === '' ? line : line + '  '))
      .join('\n');

  const commitText = (next: string) => {
    setText(next);
    // Always enable markdown when inserting a link so it renders as a hyperlink.
    updateData({ ...data, props: { ...data.props, text: preserveBreaks(next), markdown: true } });
  };

  const openLinkDialog = () => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    selectionRef.current = { start, end };
    const selected = text.slice(start, end);
    setLinkLabel(selected || '');
    setLinkUrl('https://');
    setLinkOpen(true);
  };

  const insertLink = () => {
    const { start, end } = selectionRef.current;
    const label = (linkLabel || linkUrl).trim();
    const url = linkUrl.trim();
    if (!url) {
      setLinkOpen(false);
      return;
    }
    const md = `[${label}](${url})`;
    const next = text.slice(0, start) + md + text.slice(end);
    commitText(next);
    setLinkOpen(false);
    // Restore focus and place caret after the inserted link
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        const pos = start + md.length;
        el.focus();
        el.setSelectionRange(pos, pos);
      }
    });
  };

  return (
    <BaseSidebarPanel title="Text block">
      <Stack spacing={1}>
        <TextField
          fullWidth
          multiline
          minRows={5}
          variant="outlined"
          label="Content"
          value={text}
          inputRef={textareaRef}
          onChange={(ev) => {
            const v = ev.target.value;
            setText(v);
            const outText = data.props?.markdown ? preserveBreaks(v) : v;
            updateData({ ...data, props: { ...data.props, text: outText } });
          }}
        />
        <Box>
          <Button size="small" variant="outlined" startIcon={<LinkIcon fontSize="small" />} onClick={openLinkDialog}>
            Insert link from selection
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Select text in the box above, then click to turn it into a link. Markdown will be enabled automatically.
          </Typography>
        </Box>
        {linkOpen && (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Stack spacing={1}>
              <TextField
                size="small"
                label="Link text"
                value={linkLabel}
                onChange={(e) => setLinkLabel(e.target.value)}
                placeholder="click here"
                fullWidth
              />
              <TextField
                size="small"
                label="URL"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                fullWidth
                autoFocus
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" onClick={() => setLinkOpen(false)}>
                  Cancel
                </Button>
                <Button size="small" variant="contained" onClick={insertLink}>
                  Insert link
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Stack>

      <BooleanInput
        label="Markdown (GitHub flavored)"
        defaultValue={data.props?.markdown ?? false}
        onChange={(markdown) => updateData({ ...data, props: { ...data.props, markdown } })}
      />

      <MultiStylePropertyPanel
        names={['color', 'backgroundColor', 'fontFamily', 'fontSize', 'fontWeight', 'textAlign', 'padding']}
        value={data.style}
        onChange={(style) => updateData({ ...data, style })}
      />
    </BaseSidebarPanel>
  );
}
